
import { Vue, Component, Watch } from 'vue-property-decorator';
import axios from 'axios';
import { Contact, JobStatusEnum } from '@gid/models';
import { RequestQueryBuilder, CondOperator } from '@dataui/crud-request';
import PartnerListItem from './PartnerListItem.vue';

// Jobs sitting in `open` have no partner yet, so the in-radius list alone can
// be a dead end — this many nearest partners beyond their own service radius
// are listed as well, to give dispatch someone to call.
const OUT_OF_RADIUS_LIMIT = 20;

@Component({
  components: {
    PartnerListItem,
  },
})
export default class PartnerList extends Vue {
  jobName: string | undefined = '';
  partners: object[] = [];
  partnersPotential: object[] = [];
  partnersOutOfRadius: object[] = [];
  partnerContacts: Contact[] = [];
  jobApiId: string = '';
  jobSfid: string = '';
  jobStatus: string = '';
  limit: number = 10;
  $refs!: {
    partnersModal: HTMLFormElement;
  };
  rejectionReasons: string[] = [];
  async created() {
    this.rejectionReasons = await this.fetchRejectionReasons();
  }
  get limitOptions() {
    return [5, 10, 25, 50, 100];
  }
  get outOfRadiusLimit() {
    return OUT_OF_RADIUS_LIMIT;
  }
  // The radius filter is what makes the main list "partners seeing this job".
  // Only `open` jobs get the extra bucket; every other status either already
  // has a partner or is not being dispatched yet.
  get showOutOfRadius(): boolean {
    return this.jobStatus === JobStatusEnum.OPEN;
  }
  @Watch('limit', {
    deep: true,
  })
  searchChange() {
    this.refreshPartnerList();
  }
  async viewPartners(item) {
    this.jobName = item.job.name;
    this.jobApiId = item.job.api_id;
    this.jobSfid = item.job.sfid;
    this.jobStatus = item.job.status;
    this.$refs.partnersModal.show();
    this.partners = [];
    this.partnersPotential = [];
    this.partnersOutOfRadius = [];
    this.refreshPartnerList();
  }
  async refreshPartnerList() {
    const qualifiedForJobUrl = `/api/admin/partners/qualified-for-job/${this.jobSfid}`;
    const fetchPartners = (params) =>
      axios
        .get(qualifiedForJobUrl, { params })
        .then((response) => response.data);
    // `out_of_radius` is a separate request rather than a client-side split of
    // one unfiltered list: the API orders by distance, and being in radius is
    // not a distance prefix (a far partner with a wide service area is in
    // radius, a near one with a narrow area is not), so any single limited
    // response would drop in-radius partners.
    const [partners, partnersPotential, partnersOutOfRadius] =
      await Promise.all([
        fetchPartners({ in_radius: true, limit: this.limit }),
        fetchPartners({
          only_potential: true,
          in_radius: true,
          limit: this.limit,
        }),
        this.showOutOfRadius
          ? fetchPartners({
              out_of_radius: true,
              limit: OUT_OF_RADIUS_LIMIT,
            })
          : [],
      ]);
    const accountIds = [
      ...partners,
      ...partnersPotential,
      ...partnersOutOfRadius,
    ]
      .map((p) => {
        return p.partner_id;
      })
      .filter((el) => el);
    const partnerContacts = await this.fetchContacts(accountIds);

    const matchPartnerWithContacts = (partners, contacts) => {
      return partners.map((p) => {
        const thisPartnerContacts = contacts.filter(
          (pc) =>
            pc.accountId === p.partner_id &&
            (pc.mobilePhone || pc.phone) &&
            pc.email,
        );
        let pickedPartnerContacts: Contact[] | object = [];
        if (thisPartnerContacts.length) {
          pickedPartnerContacts = thisPartnerContacts;
        }
        return {
          ...p,
          ...{
            contacts: pickedPartnerContacts,
          },
        };
      });
    };
    this.partners = matchPartnerWithContacts(partners, partnerContacts);
    this.partnersPotential = matchPartnerWithContacts(
      partnersPotential,
      partnerContacts,
    );
    this.partnersOutOfRadius = matchPartnerWithContacts(
      partnersOutOfRadius,
      partnerContacts,
    );
  }
  async fetchRejectionReasons() {
    const rejectJobReasons = 'Reject_Job_Reasons';
    return axios
      .get('/data-api/sf-picklist', {
        params: {
          api_names: rejectJobReasons,
          values_only: true,
        },
      })
      .then((r) =>
        r.data?.[rejectJobReasons]?.Metadata?.customValue
          .map((v) => v.valueName)
          .sort((a, b) => a.localeCompare(b)),
      )
      .catch(() => {
        return [];
      });
  }
  async fetchContacts(accountIds) {
    const queryString = RequestQueryBuilder.create({
      filter: {
        field: 'account.id',
        operator: CondOperator.IN,
        value: accountIds,
      },
      sort: [{ field: 'createdAt', order: 'ASC' }],
    }).query();
    return axios
      .get(`/data-api/contact?${queryString}`)
      .then((response) => {
        return response.data;
      })
      .catch(() => {
        return [];
      });
  }
}
